/**
 * Attachment Hooks
 * React Query hooks for managing audit finding attachments (upload, list, delete, download)
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "../lib/apiClient";
import { AuditFindingAttachment } from "../types/audit";

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface AttachmentWithDownloadUrl extends AuditFindingAttachment {
  download_url?: string;
}

export interface UploadAttachmentInput {
  file: File;
  description?: string;
}

// ============================================================================
// QUERY: Get attachments for a finding
// ============================================================================

export function useAttachments(findingId?: string) {
  return useQuery({
    queryKey: ["attachments", findingId],
    queryFn: async () => {
      if (!findingId) return [];

      const response = await apiClient.get<{
        data: { attachments: AuditFindingAttachment[] };
      }>(`/audit/findings/${findingId}/attachments`);

      return response.data.data.attachments;
    },
    enabled: !!findingId,
  });
}

// ============================================================================
// MUTATION: Upload attachment
// ============================================================================

export function useUploadAttachment(findingId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    // US-081: error fallback centralized here; AttachmentUpload catch is sole caller.
    meta: { errorFallback: "Failed to upload attachment" },
    mutationFn: async (input: UploadAttachmentInput) => {
      const formData = new FormData();
      formData.append("file", input.file);
      if (input.description) {
        formData.append("description", input.description);
      }

      const response = await apiClient.post<{
        data: { attachment: AuditFindingAttachment };
      }>(`/audit/findings/${findingId}/attachments`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      return response.data.data.attachment;
    },
    onSuccess: () => {
      // Invalidate attachments list
      queryClient.invalidateQueries({ queryKey: ["attachments", findingId] });
      // Invalidate finding detail (to update attachment count)
      queryClient.invalidateQueries({ queryKey: ["finding", findingId] });
    },
  });
}

// ============================================================================
// MUTATION: Delete attachment
// ============================================================================

export function useDeleteAttachment(findingId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    // US-081: error fallback centralized here; AttachmentList catch is sole caller.
    meta: { errorFallback: "Failed to delete attachment" },
    mutationFn: async (attachmentId: string) => {
      await apiClient.delete(
        `/audit/findings/${findingId}/attachments/${attachmentId}`,
      );
    },
    onSuccess: () => {
      // Invalidate attachments list
      queryClient.invalidateQueries({ queryKey: ["attachments", findingId] });
      // Invalidate finding detail
      queryClient.invalidateQueries({ queryKey: ["finding", findingId] });
    },
  });
}

// ============================================================================
// QUERY: Get attachment download URL
// ============================================================================

export function useAttachmentDownloadUrl(
  findingId: string,
  attachmentId?: string,
) {
  return useQuery({
    queryKey: ["attachment-download", findingId, attachmentId],
    queryFn: async () => {
      if (!attachmentId) return null;

      const response = await apiClient.get<{
        data: { attachment: AttachmentWithDownloadUrl };
      }>(`/audit/findings/${findingId}/attachments/${attachmentId}`);

      return response.data.data.attachment;
    },
    enabled: !!attachmentId && !!findingId,
    staleTime: 30 * 60 * 1000, // 30 minutes (signed URLs valid for 1 hour)
  });
}

// ============================================================================
// HELPER: Download attachment
// ============================================================================

export async function downloadAttachment(
  findingId: string,
  attachmentId: string,
  fileName: string,
) {
  try {
    // Get signed download URL
    const response = await apiClient.get<{
      data: { attachment: AttachmentWithDownloadUrl };
    }>(`/audit/findings/${findingId}/attachments/${attachmentId}`);

    const downloadUrl = response.data.data.attachment.download_url;

    if (!downloadUrl) {
      throw new Error("Download URL not available");
    }

    // Trigger browser download
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = fileName;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error("Failed to download attachment:", error);
    throw error;
  }
}
